import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ChevronsDownUp, ChevronsUpDown, CornerDownRight, MessagesSquare, Paperclip } from "lucide-react";
import { api, type UnterhaltungsNachricht } from "../api";
import { datumKurz, datumLang } from "../lib/datum";
import { MailRahmen } from "./MailRahmen";
import { Avatar } from "./Avatar";

/** Ab so vielen älteren Nachrichten wird die Mitte zu einer Zeile zusammengefaltet. */
const FALTEN_AB = 5;

/** „Petra Vogt <p@x.de>" → „Petra Vogt", „p@x.de" bleibt. */
function kurzname(adresse: string): string {
  const name = adresse.replace(/<[^>]*>/, "").replace(/"/g, "").trim();
  return name || adresse;
}

/**
 * Die ganze Unterhaltung zu einer Mail als Verlauf — chronologisch, mit
 * deinen eigenen Antworten aus „Gesendet". Jede Nachricht zeigt nur ihren
 * neuen Text; das Zitat steht ja schon in der Karte darüber. Die direkt
 * vorangehende ist aufgeklappt, bei langen Verläufen wird die Mitte gefaltet
 * (wie man es aus Gmail kennt).
 */
export function Unterhaltung({ messageId, onOpen }: { messageId: number; onOpen?: (id: number) => void }) {
  const [nachrichten, setNachrichten] = useState<UnterhaltungsNachricht[] | null>(null);
  const [offen, setOffen] = useState<Set<number>>(new Set());
  const [original, setOriginal] = useState<Set<number>>(new Set());
  const [eingeklappt, setEingeklappt] = useState(false);
  const [mitteOffen, setMitteOffen] = useState(false);

  useEffect(() => {
    let aktiv = true;
    setNachrichten(null);
    setMitteOffen(false);
    setOriginal(new Set());
    api.unterhaltung(messageId)
      .then((r) => {
        if (!aktiv) return;
        setNachrichten(r.nachrichten);
        // Die Nachricht direkt vor der geöffneten aufklappen — auf die antwortet man.
        const i = r.nachrichten.findIndex((n) => n.id === messageId);
        const vorher = r.nachrichten[i > 0 ? i - 1 : r.nachrichten.length - 1];
        setOffen(new Set(vorher && vorher.id !== messageId ? [vorher.id] : []));
      })
      .catch(() => aktiv && setNachrichten([]));
    return () => { aktiv = false; };
  }, [messageId]);

  const beteiligte = useMemo(() => {
    const m = new Map<string, { email: string; name: string; eigene: boolean }>();
    for (const n of nachrichten ?? []) {
      const k = n.eigene ? "__du" : n.from_email.toLowerCase();
      if (!m.has(k)) m.set(k, { email: n.from_email, name: n.from_name || n.from_email, eigene: n.eigene });
    }
    return [...m.values()];
  }, [nachrichten]);

  const andere = (nachrichten ?? []).filter((n) => n.id !== messageId);
  if (!nachrichten || andere.length === 0) return null;

  const umschalten = (menge: Set<number>, setzen: (s: Set<number>) => void, id: number) => {
    const n = new Set(menge);
    if (n.has(id)) n.delete(id); else n.add(id);
    setzen(n);
  };

  const alleOffen = andere.every((n) => offen.has(n.id));
  const allesUmschalten = () => {
    if (alleOffen) setOffen(new Set());
    else { setOffen(new Set(andere.map((n) => n.id))); setMitteOffen(true); }
  };

  // Lange Verläufe: die erste und die letzten beiden vor der aktuellen bleiben
  // sichtbar, dazwischen eine Zeile „n ältere Nachrichten".
  const iAktuell = nachrichten.findIndex((n) => n.id === messageId);
  const davor = iAktuell >= 0 ? iAktuell : nachrichten.length;
  const falten = !mitteOffen && davor > FALTEN_AB;
  const gefaltet = falten ? nachrichten.slice(1, davor - 2) : [];
  const gefaltetIds = new Set(gefaltet.map((n) => n.id));

  const zumText = () => document.querySelector(".reader-content")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section className="unterhaltung" aria-label="Unterhaltung">
      <div className="unterhaltung-kopf">
        <button type="button" className="unterhaltung-titel" aria-expanded={!eingeklappt} onClick={() => setEingeklappt((v) => !v)}>
          <MessagesSquare aria-hidden="true" />
          <b>Unterhaltung</b>
          <span>{nachrichten.length} Nachrichten</span>
        </button>
        <span className="avatar-stapel" title={beteiligte.map((b) => (b.eigene ? "Du" : b.name)).join(", ")}>
          {beteiligte.slice(0, 4).map((b) => (
            <Avatar key={b.eigene ? "__du" : b.email} email={b.email} name={b.name} eigene={b.eigene} groesse="s" />
          ))}
          {beteiligte.length > 4 && <span className="avatar avatar-mehr" data-groesse="s">+{beteiligte.length - 4}</span>}
        </span>
        <span className="spacer" />
        {!eingeklappt && (
          <button type="button" className="btn quiet klein" onClick={allesUmschalten}>
            {alleOffen ? <ChevronsDownUp aria-hidden="true" /> : <ChevronsUpDown aria-hidden="true" />}
            <span className="nur-breit">{alleOffen ? "Alle zuklappen" : "Alle aufklappen"}</span>
          </button>
        )}
      </div>

      {!eingeklappt && (
        <ol className="verlauf">
          {nachrichten.map((n, i) => {
            if (gefaltetIds.has(n.id)) {
              if (n.id !== gefaltet[0]!.id) return null;
              return (
                <li key="gefaltet" className="verlauf-falte">
                  <button type="button" onClick={() => setMitteOffen(true)}>
                    <span className="verlauf-falte-zahl">{gefaltet.length}</span>
                    ältere Nachrichten anzeigen
                  </button>
                </li>
              );
            }
            const aktuell = n.id === messageId;
            const auf = offen.has(n.id);
            const name = n.eigene ? "Du" : n.from_name || n.from_email;
            const an = n.to.map(kurzname).slice(0, 2).join(", ") + (n.to.length > 2 ? ` +${n.to.length - 2}` : "");
            return (
              <li
                key={n.id}
                className="verlauf-eintrag"
                data-eigene={n.eigene}
                data-aktuell={aktuell}
                data-offen={auf || undefined}
                data-ungelesen={n.seen === 0 && !n.eigene}
                data-letzter={i === nachrichten.length - 1 || undefined}
              >
                <Avatar email={n.from_email} name={n.from_name || n.from_email} eigene={n.eigene} groesse="m" className="verlauf-avatar" />
                <div className="verlauf-karte">
                  <button
                    type="button"
                    className="verlauf-karte-kopf"
                    aria-expanded={aktuell ? undefined : auf}
                    onClick={() => (aktuell ? zumText() : umschalten(offen, setOffen, n.id))}
                  >
                    <span className="verlauf-von">
                      <b>{name}</b>
                      {an && <span className="verlauf-an">an {an}</span>}
                    </span>
                    {n.anhaenge > 0 && (
                      <span className="verlauf-clip" title={`${n.anhaenge} ${n.anhaenge === 1 ? "Anhang" : "Anhänge"}`}>
                        <Paperclip aria-hidden="true" />{n.anhaenge > 1 ? n.anhaenge : ""}
                      </span>
                    )}
                    <time dateTime={n.date} title={datumLang(n.date)}>{datumKurz(n.date)}</time>
                    {aktuell ? (
                      <span className="verlauf-hier"><ArrowDown aria-hidden="true" /> Diese Nachricht — steht unten</span>
                    ) : !auf ? (
                      <span className="verlauf-vorschau">{n.text.replace(/\s+/g, " ").slice(0, 160) || "(kein Text)"}</span>
                    ) : null}
                  </button>
                  {auf && !aktuell && (
                    <div className="verlauf-inhalt">
                      {original.has(n.id) && n.body_html
                        ? <MailRahmen html={n.body_html} bilderFrei={false} />
                        : <pre className="verlauf-text">{n.text || "(kein Text)"}</pre>}
                      <div className="verlauf-aktionen">
                        {n.body_html && (
                          <button type="button" className="btn quiet klein" onClick={() => umschalten(original, setOriginal, n.id)}>
                            {original.has(n.id) ? "Nur neuen Text" : "Ganze Mail anzeigen"}
                          </button>
                        )}
                        {onOpen && (
                          <button type="button" className="btn quiet klein" onClick={() => onOpen(n.id)}>
                            <CornerDownRight aria-hidden="true" /> Öffnen{n.anhaenge > 0 ? " (mit Anhängen)" : ""}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
